import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Truck, Package, Gauge } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const HERO_IMG = '/hero-truck.jpg';

const roles = [
  {
    icon: Truck,
    title: 'For Drivers',
    desc: 'Find loads, navigate with GPS, track your earnings. Built for the road.',
    color: 'text-[#E5A100]',
    bg: 'bg-[#E5A100]/10',
    border: 'border-[#E5A100]/20 hover:border-[#E5A100]/50',
    link: '/for-drivers',
    cta: 'Start Driving',
  },
  {
    icon: Package,
    title: 'For Shippers',
    desc: 'Book shipments in minutes. Real-time tracking and transparent pricing.',
    color: 'text-white',
    bg: 'bg-[#E5A100]/10',
    border: 'border-white/20 hover:border-white/50',
    link: '/for-shippers',
    cta: 'Ship Now',
  },
  {
    icon: Gauge,
    title: 'For Fleet Managers',
    desc: 'Command your fleet. Driver management, settlements, and analytics.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20 hover:border-emerald-500/50',
    link: '/for-fleets',
    cta: 'Manage Fleet',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B] text-white overflow-hidden" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 flex items-center justify-between px-6 md:px-12 py-5 z-20 bg-[#09090B]/70 backdrop-blur-md" data-testid="landing-nav">
        <BrandLogo size="default" darkBg={true} />
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate('/login')} data-testid="nav-login-btn">
            Log In
          </Button>
          <Button className="bg-[#E5A100] hover:bg-yellow-500 text-black font-semibold rounded-full px-6"
            onClick={() => navigate('/register')} data-testid="nav-register-btn">
            Get Started <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-24 md:pt-36 pb-24" data-testid="landing-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Highway logistics" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl">
          <p className="text-[#E5A100] text-sm font-medium tracking-wide mb-5 uppercase">
            The future of freight
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
            Move freight.<br />
            <span className="bg-gradient-to-r from-[#E5A100] via-white to-[#E5A100] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Not paperwork.
            </span>
          </h1>
          <p className="text-zinc-400 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed font-light">
            The all-in-one logistics platform for trucking companies. Track loads, manage drivers,
            and automate settlements from a single command center.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button className="bg-[#E5A100] hover:bg-yellow-500 text-black font-semibold rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/register')} data-testid="hero-cta-btn">
              Start Free <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')} data-testid="hero-login-btn">
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Role Cards */}
      <section className="px-6 md:px-12 pb-20" data-testid="landing-roles">
        <h2 className="text-base md:text-lg font-bold tracking-tight mb-8 text-center">
          One platform. <span className="bg-gradient-to-r from-[#E5A100] via-white to-[#E5A100] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">Three views.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {roles.map((r, i) => (
            <button key={i} onClick={() => navigate(r.link)}
              className={`text-left border ${r.border} bg-zinc-900/30 p-8 rounded-2xl transition-all duration-300 hover:bg-zinc-900/60 group`}
              data-testid={`role-card-${r.link.slice(5)}`}>
              <div className={`w-12 h-12 rounded-xl ${r.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <r.icon className={`w-6 h-6 ${r.color}`} strokeWidth={1.5} />
              </div>
              <h3 className={`text-lg font-bold ${r.color} mb-2`}>{r.title}</h3>
              <p className="text-zinc-500 text-sm mb-5 leading-relaxed">{r.desc}</p>
              <span className={`inline-flex items-center text-xs font-semibold ${r.color} tracking-wider`}>
                {r.cta} <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <BrandLogo size="sm" darkBg={true} className="opacity-50" />
        <div className="flex items-center gap-6 text-xs text-zinc-600">
          <button onClick={() => navigate('/for-drivers')} className="hover:text-zinc-400 transition-colors">For Drivers</button>
          <button onClick={() => navigate('/for-shippers')} className="hover:text-zinc-400 transition-colors">For Shippers</button>
          <button onClick={() => navigate('/for-fleets')} className="hover:text-zinc-400 transition-colors">For Fleets</button>
        </div>
        <p className="text-zinc-700 text-xs">Freight Command Center</p>
      </footer>
    </div>
  );
}
